import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Star, Loader2 } from 'lucide-react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';

export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    event_service: '',
    rating: 5,
    text: '',
    consent_privacy: false,
    honeypot: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [reviewsRes, settingsRes] = await Promise.all([
          api.get('/public/reviews'),
          api.get('/public/settings')
        ]);
        setReviews(reviewsRes.data);
        setSettings(settingsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Helper per ottenere testi CMS con fallback
  const t = (key, fallback) => settings?.texts?.[key] || fallback;

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const setRating = (rating) => {
    setFormData(prev => ({ ...prev, rating }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.honeypot) {
      toast.error(t('msg_error_generic', 'Richiesta non valida'));
      return;
    }

    if (!formData.name || !formData.event_service || !formData.text) {
      toast.error(t('msg_error_required', 'Compila tutti i campi obbligatori'));
      return;
    }

    if (!formData.consent_privacy) {
      toast.error(t('msg_error_privacy', 'Devi acconsentire alla privacy policy'));
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/reviews/', formData);
      setSubmitted(true);
      toast.success(t('msg_review_success', 'Grazie! La tua recensione è stata inviata e sarà pubblicata dopo la moderazione.'));
      setFormData({
        name: '',
        event_service: '',
        rating: 5,
        text: '',
        consent_privacy: false,
        honeypot: ''
      });
    } catch (error) {
      console.error('Submit error:', error);
      const message = error.response?.data?.detail || t('msg_error_generic', 'Errore nell\'invio della recensione');
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Caricamento...</div>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-24" data-testid="reviews-page">
      {/* Header */}
      <div className="max-w-[1800px] mx-auto px-6 lg:px-12 mb-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <span className="text-sm tracking-[0.2em] uppercase text-muted-foreground">
            {t('reviews_page_label', 'Testimonianze')}
          </span>
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl mt-4 mb-6">
            {t('page_reviews_title', 'Cosa Dicono di Me')}
          </h1>
          <p className="font-accent text-xl text-muted-foreground max-w-2xl mx-auto">
            {t('page_reviews_subtitle', 'La soddisfazione dei miei clienti è la mia priorità. Ecco alcune delle loro esperienze.')}
          </p>
        </motion.div>
      </div>

      {/* Reviews Grid */}
      <div className="max-w-[1800px] mx-auto px-6 lg:px-12 mb-16">
        {reviews.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {reviews.map((review, index) => (
              <motion.div
                key={review.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                className="bg-muted/50 p-8 lg:p-10"
                data-testid={`review-card-${index}`}
              >
                <div className="flex gap-1 mb-6 star-rating">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      size={18}
                      fill={i < review.rating ? 'currentColor' : 'none'}
                    />
                  ))}
                </div>
                <p className="font-accent text-lg italic mb-6 text-foreground/90">
                  "{review.text}"
                </p>
                <div>
                  <p className="font-medium">{review.name}</p>
                  <p className="text-sm text-muted-foreground">{review.event_service}</p>
                  {review.source && review.source !== 'Sito Web' && (
                    <p className="text-xs text-muted-foreground mt-1">via {review.source}</p>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 text-muted-foreground">
            {t('reviews_empty', 'Nessuna recensione ancora. Sii il primo a lasciare una recensione!')}
          </div>
        )}
      </div>

      {/* Write Review Section */}
      <div className="max-w-3xl mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="bg-card border border-border p-8 lg:p-12"
        >
          {!showForm && !submitted ? (
            <div className="text-center">
              <h2 className="font-heading text-3xl mb-4">{t('reviews_cta_title', 'Hai lavorato con me?')}</h2>
              <p className="text-muted-foreground mb-8">
                {t('reviews_cta_subtitle', 'Mi farebbe piacere sapere cosa ne pensi. Lascia una recensione!')}
              </p>
              <Button onClick={() => setShowForm(true)} data-testid="write-review-btn">
                {t('btn_write_review', 'Scrivi una Recensione')}
              </Button>
            </div>
          ) : submitted ? (
            <div className="text-center py-8" data-testid="review-submitted">
              <h2 className="font-heading text-3xl mb-4">{t('review_success_title', 'Grazie!')}</h2>
              <p className="text-muted-foreground">
                {t('msg_review_success', 'La tua recensione è stata inviata e sarà pubblicata dopo la moderazione.')}
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} data-testid="review-form">
              <h2 className="font-heading text-3xl mb-8 text-center">{t('review_form_title', 'Lascia una Recensione')}</h2>
              
              <div className="mb-6">
                <Label className="mb-3 block">{t('form_name', 'Il tuo nome')} *</Label>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder={t('form_name_placeholder', 'Nome o nickname')}
                  className="input-artistic"
                  data-testid="review-name-input"
                />
              </div>

              <div className="mb-6">
                <Label className="mb-3 block">{t('form_service', 'Servizio / Evento')} *</Label>
                <Input
                  name="event_service"
                  value={formData.event_service}
                  onChange={handleInputChange}
                  placeholder={t('form_service_placeholder', 'Es: Matrimonio, Ritratto aziendale...')}
                  className="input-artistic"
                  data-testid="review-service-input"
                />
              </div>

              <div className="mb-6">
                <Label className="mb-3 block">{t('form_rating', 'Valutazione')} *</Label>
                <div className="flex gap-2" data-testid="review-rating">
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <button
                      key={rating}
                      type="button"
                      onClick={() => setRating(rating)}
                      className="p-1 star-rating"
                      data-testid={`rating-star-${rating}`}
                    >
                      <Star
                        size={28}
                        fill={rating <= formData.rating ? 'currentColor' : 'none'}
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-6">
                <Label className="mb-3 block">{t('form_review_text', 'La tua recensione')} *</Label>
                <Textarea
                  name="text"
                  value={formData.text}
                  onChange={handleInputChange}
                  rows={5}
                  placeholder={t('form_review_placeholder', 'Racconta la tua esperienza...')}
                  className="resize-none"
                  data-testid="review-text-input"
                />
              </div>

              <div className="mb-8">
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox
                    name="consent_privacy"
                    checked={formData.consent_privacy}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, consent_privacy: checked }))}
                    data-testid="review-consent-checkbox"
                  />
                  <span className="text-sm text-muted-foreground">
                    {t('form_privacy_consent', 'Acconsento al trattamento dei miei dati personali secondo la')}{' '}
                    <a href="/privacy" className="underline">privacy policy</a>. *
                  </span>
                </label>
              </div>

              {/* Honeypot */}
              <input
                type="text"
                name="honeypot"
                value={formData.honeypot}
                onChange={handleInputChange}
                className="absolute opacity-0 pointer-events-none"
                tabIndex={-1}
                autoComplete="off"
              />

              <div className="flex gap-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowForm(false)}
                >
                  {t('btn_cancel', 'Annulla')}
                </Button>
                <Button type="submit" disabled={submitting} data-testid="submit-review-btn">
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="mr-2 animate-spin" />
                      {t('btn_sending', 'Invio...')}
                    </>
                  ) : (
                    t('btn_submit_review', 'Invia Recensione')
                  )}
                </Button>
              </div>
            </form>
          )}
        </motion.div>
      </div>
    </div>
  );
}
